import { FormEvent, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { adminApi, ApiError, type UserDetail } from '../lib/api';
import { useProductScope } from '../context/ProductScopeContext';
import { canAssignPlan, canDeleteUsers, canGrantCredits, isSuperAdmin } from '../lib/auth';
import { useOperatorPermissions } from '../hooks/useOperatorPermissions';
import { useToast } from '../context/ToastContext';
import {
  copy,
  formatLanguage,
  formatPlan,
  formatUserSubtitle,
  formatYesNo,
  translateApiError,
} from '../lib/copy';
import { cn, formatDate, formatNumber } from '../lib/utils';
import { ClientPagination, paginate } from '../components/ClientPagination';
import { ErrorState, LoadingState, PageHeader, StatusBadge } from '../components/ui';
import { resolveAppPlanAssignment } from '../lib/plan-assignment';

type Tab = 'profile' | 'identities' | 'billing' | 'sessions' | 'credits' | 'legal';

export function UserDetailPage() {
  const { id = '' } = useParams();
  const scope = useProductScope();
  const isAppScope = scope === 'app';
  const isStudioScope = scope === 'studio';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const { canPatchUser, canRevokeSessions } = useOperatorPermissions();
  const [tab, setTab] = useState<Tab>('profile');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [planId, setPlanId] = useState('');
  const [planMonths, setPlanMonths] = useState('12');
  const [planReason, setPlanReason] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planSuccess, setPlanSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [identityPage, setIdentityPage] = useState(0);
  const identityPageSize = 10;
  const [editOnboarded, setEditOnboarded] = useState(false);
  const [editLanguage, setEditLanguage] = useState('ru');

  const agentsQuery = useQuery({
    queryKey: ['agents', 'user', id],
    queryFn: () => adminApi.agents({ user_id: id, limit: 5 }),
    enabled: isAppScope && Boolean(id) && tab === 'profile',
  });

  const ledgerQuery = useQuery({
    queryKey: ['users', id, 'credit-ledger'],
    queryFn: () => adminApi.userCreditLedger(id, 50),
    enabled: !isStudioScope && Boolean(id) && tab === 'credits',
  });

  const patchUserMutation = useMutation({
    mutationFn: (payload: { onboarded?: boolean; language?: string }) =>
      adminApi.patchUser(id, payload),
    onSuccess: () => {
      pushToast(copy.userDetail.profileSaved, 'success');
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
    onError: (err: unknown) => {
      pushToast(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.updateUser,
        'error',
      );
    },
  });

  const revokeSessionsMutation = useMutation({
    mutationFn: () => adminApi.revokeUserSessions(id),
    onSuccess: () => {
      pushToast(copy.userDetail.revokeSessionsSuccess, 'success');
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
    onError: () => pushToast(copy.errors.revokeSessions, 'error'),
  });

  useEffect(() => {
    setIdentityPage(0);
  }, [id, tab]);

  useEffect(() => {
    if (isStudioScope && (tab === 'billing' || tab === 'credits')) {
      setTab('profile');
    }
  }, [isStudioScope, tab]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', scope, id],
    queryFn: () => {
      if (scope === 'app') return adminApi.appUser(id) as Promise<UserDetail>;
      if (scope === 'studio') return adminApi.studioUser(id) as Promise<UserDetail>;
      return adminApi.platformUser(id) as Promise<UserDetail>;
    },
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!data?.user) return;
    setEditOnboarded(data.user.onboarded);
    setEditLanguage(data.user.language);
  }, [data?.user]);

  const plansQuery = useQuery({
    queryKey: ['admin', 'plans', scope],
    queryFn: () => (scope === 'studio' ? adminApi.studioPlans() : adminApi.appPlans()),
  });

  useEffect(() => {
    if (!data?.user.plan || planId) return;
    setPlanId(data.user.plan);
  }, [data?.user.plan, planId]);

  const grantMutation = useMutation({
    mutationFn: (payload: { amount: number; reason: string }) => {
      if (!isAppScope) {
        throw new ApiError('APP credits are not available in the Studio admin', 403);
      }
      const workspaceId =
        (data as UserDetail & { personal_workspace_id?: string } | undefined)?.personal_workspace_id ??
        '';
      return adminApi.grantCredits(
        id,
        payload.amount,
        payload.reason,
        workspaceId,
        `admin-grant-${Date.now()}`,
      );
    },
    onSuccess: () => {
      setFormSuccess(copy.userDetail.grantSuccess);
      setFormError(null);
      setAmount('');
      setReason('');
      queryClient.invalidateQueries({ queryKey: ['users', id] });
    },
    onError: (err: unknown) => {
      setFormSuccess(null);
      setFormError(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.grantCredits,
      );
    },
  });

  const impersonateMutation = useMutation({
    mutationFn: () => {
      if (isStudioScope) {
        throw new ApiError('Studio user impersonation is not available in this admin', 403);
      }
      return adminApi.impersonate(id);
    },
    onSuccess: (result) => {
      window.open(result.impersonation_url, '_blank', 'noopener,noreferrer');
    },
    onError: (err: unknown) => {
      setFormError(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.impersonate,
      );
    },
  });

  const assignPlanMutation = useMutation({
    mutationFn: (payload: {
      workspaceId: string;
      productPlanId: string;
      plan_id: string;
      months: number;
      reason: string;
    }) => {
      if (!isAppScope) {
        throw new ApiError('APP plans are not available in the Studio admin', 403);
      }
      return adminApi.assignAppPlan(id, {
        ...payload,
        idempotencyKey: `admin-ui-${Date.now()}`,
      });
    },
    onSuccess: () => {
      setPlanSuccess(copy.userDetail.assignPlanSuccess);
      setPlanError(null);
      queryClient.invalidateQueries({ queryKey: ['users', id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err: unknown) => {
      setPlanSuccess(null);
      setPlanError(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.assignPlan,
      );
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (payload: { reason: string }) => adminApi.deleteUser(id, payload.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate(`/${scope}/users`);
    },
    onError: (err: unknown) => {
      setDeleteError(
        err instanceof ApiError ? translateApiError(err.message) : copy.errors.deleteUser,
      );
    },
  });

  const handleGrant = (e: FormEvent) => {
    e.preventDefault();
    if (!isAppScope) return;
    setFormError(null);
    setFormSuccess(null);
    const parsed = Number(amount);
    if (!Number.isInteger(parsed) || parsed < 1) {
      setFormError(copy.errors.grantAmount);
      return;
    }
    if (reason.trim().length < 3) {
      setFormError(copy.errors.grantReason);
      return;
    }
    grantMutation.mutate({ amount: parsed, reason: reason.trim() });
  };

  const handleAssignPlan = (e: FormEvent) => {
    e.preventDefault();
    if (!isAppScope) return;
    setPlanError(null);
    setPlanSuccess(null);
    const months = Number(planMonths);
    if (!planId) {
      setPlanError(copy.errors.assignPlan);
      return;
    }
    if (!Number.isInteger(months) || months < 1) {
      setPlanError(copy.errors.assignPlan);
      return;
    }
    if (planReason.trim().length < 3) {
      setPlanError(copy.errors.grantReason);
      return;
    }
    const selectedPlan = plansQuery.data?.items.find((plan) => plan.code === planId);
    const resolved = resolveAppPlanAssignment({
      workspaceId: data?.personal_workspace_id,
      selectedPlan,
    });
    if (!resolved.ok) {
      setPlanError(
        resolved.reason === 'workspace'
          ? copy.errors.assignPlanWorkspace
          : copy.errors.assignPlanOption,
      );
      return;
    }
    assignPlanMutation.mutate({
      workspaceId: resolved.workspaceId,
      productPlanId: resolved.productPlanId,
      plan_id: planId,
      months,
      reason: planReason.trim(),
    });
  };

  const handleDeleteUser = (e: FormEvent) => {
    e.preventDefault();
    setDeleteError(null);
    if (deleteConfirm.trim() !== 'DELETE') {
      setDeleteError(copy.userDetail.deleteUserConfirm);
      return;
    }
    if (deleteReason.trim().length < 3) {
      setDeleteError(copy.errors.grantReason);
      return;
    }
    if (!window.confirm(copy.userDetail.deleteUserHint)) {
      return;
    }
    deleteUserMutation.mutate({ reason: deleteReason.trim() });
  };

  if (isLoading) return <LoadingState label="Загрузка пользователя..." />;
  if (isError || !data) {
    return <ErrorState message={copy.errors.loadUser} onRetry={() => refetch()} />;
  }

  const subtitle = formatUserSubtitle(data.user.username);
  const tabs: { id: Tab; label: string }[] = [
    { id: 'profile', label: copy.userDetail.profile },
    { id: 'identities', label: copy.userDetail.identities },
    { id: 'sessions', label: copy.userDetail.sessions },
    { id: 'legal', label: copy.userDetail.legal },
  ];
  if (!isStudioScope) {
    tabs.splice(2, 0, { id: 'billing', label: copy.userDetail.billing });
    tabs.splice(4, 0, { id: 'credits', label: copy.userDetail.creditsLedger });
  }
  const usersPath = `/${scope}/users`;
  const usagePath = `/${scope}/usage?user_id=${data.user.id}`;

  return (
    <div>
      <Link to={usersPath} className="mb-4 inline-flex items-center gap-1 text-sm text-ops-muted hover:text-ops-text">
        <ArrowLeft className="h-4 w-4" />
        {copy.userDetail.back}
      </Link>
      <PageHeader
        title={data.user.first_name}
        description={subtitle ?? undefined}
        actions={
          isSuperAdmin() && !isStudioScope ? (
            <button
              type="button"
              className="ops-btn-primary"
              disabled={impersonateMutation.isPending}
              onClick={() => impersonateMutation.mutate()}
            >
              <ExternalLink className="h-4 w-4" />
              {copy.userDetail.impersonate}
            </button>
          ) : null
        }
      />

      <div className="mb-4 flex gap-2 border-b border-ops-border">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              'ops-tab',
              tab === item.id ? 'ops-tab--active' : 'ops-tab--inactive',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="ops-card space-y-3 text-sm">
            <h2 className="text-sm font-semibold">{copy.userDetail.account}</h2>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.plan}</dt>
                <dd><StatusBadge status={data.user.plan} kind="plan" /></dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.username}</dt>
                <dd>{data.user.username ? `@${data.user.username}` : copy.common.notAvailable}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.telegram}</dt>
                <dd>
                  {data.user.telegram_id
                    ? copy.userDetail.telegramConnected
                    : copy.userDetail.telegramNotConnected}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.language}</dt>
                <dd>{formatLanguage(data.user.language)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.onboarded}</dt>
                <dd>{formatYesNo(data.user.onboarded)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.created}</dt>
                <dd>{formatDate(data.user.created_at)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.timezone}</dt>
                <dd>{data.user.timezone}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.memberships}</dt>
                <dd>{data.user.memberships_count}</dd>
              </div>
            </dl>
            <Link to={usagePath} className="mt-3 inline-block text-sm text-ops-text hover:underline">
              {copy.userDetail.usageLink}
            </Link>
          </div>
          <div className="ops-card space-y-3 text-sm">
            <h2 className="text-sm font-semibold">{copy.userDetail.usage}</h2>
            <dl className="space-y-2">
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.creditsBalance}</dt>
                <dd>{formatNumber(data.user.credits_balance)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.creditsLimit}</dt>
                <dd>{formatNumber(data.user.credits_limit)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ops-muted">{copy.userDetail.fairUse}</dt>
                <dd>
                  {formatNumber(data.user.fair_use_used)} / {formatNumber(data.user.fair_use_limit)}
                </dd>
              </div>
              {!isStudioScope ? (
                <>
                  <div className="flex justify-between">
                    <dt className="text-ops-muted">{copy.userDetail.agents}</dt>
                    <dd>{data.user.agents_count}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-ops-muted">{copy.userDetail.payments}</dt>
                    <dd>{data.user.payments_count}</dd>
                  </div>
                </>
              ) : null}
            </dl>
          </div>
          {canPatchUser ? (
            <div className="ops-card space-y-3 text-sm md:col-span-2">
              <h2 className="text-sm font-semibold">{copy.userDetail.editProfile}</h2>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editOnboarded}
                  onChange={(e) => setEditOnboarded(e.target.checked)}
                />
                {copy.userDetail.onboarded}
              </label>
              <select
                className="ops-input max-w-xs"
                value={editLanguage}
                onChange={(e) => setEditLanguage(e.target.value)}
              >
                <option value="ru">ru</option>
                <option value="en">en</option>
              </select>
              <button
                type="button"
                className="ops-btn-primary"
                disabled={patchUserMutation.isPending}
                onClick={() =>
                  patchUserMutation.mutate({
                    onboarded: editOnboarded,
                    language: editLanguage,
                  })
                }
              >
                {copy.userDetail.saveProfile}
              </button>
            </div>
          ) : null}
          {agentsQuery.data && agentsQuery.data.items.length > 0 ? (
            <div className="ops-card md:col-span-2">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">{copy.userDetail.userAgentsTitle}</h2>
                <Link to={`/app/agents?user_id=${id}`} className="text-xs text-ops-muted hover:text-ops-text">
                  {copy.userDetail.allAgents}
                </Link>
              </div>
              <table className="ops-table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {agentsQuery.data.items.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        <Link to={`/app/agents/${agent.id}`} className="hover:underline">
                          {agent.name}
                        </Link>
                      </td>
                      <td>{agent.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}

      {tab === 'identities' ? (
        <div className="ops-card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>{copy.userDetail.provider}</th>
                  <th>{copy.userDetail.email}</th>
                  <th>{copy.userDetail.verified}</th>
                  <th>{copy.userDetail.lastUsed}</th>
                </tr>
              </thead>
              <tbody>
                {paginate(data.identities, identityPage, identityPageSize).map((identity) => (
                  <tr key={identity.id}>
                    <td><StatusBadge status={identity.provider} kind="provider" /></td>
                    <td>{identity.email ?? copy.common.notAvailable}</td>
                    <td>
                      {identity.verified_at ? formatDate(identity.verified_at) : copy.common.no}
                    </td>
                    <td className="text-ops-muted">
                      {identity.last_used_at ? formatDate(identity.last_used_at) : copy.common.notAvailable}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.identities.length > identityPageSize ? (
            <ClientPagination
              page={identityPage}
              pageSize={identityPageSize}
              total={data.identities.length}
              onPageChange={setIdentityPage}
            />
          ) : null}
        </div>
      ) : null}

      {tab === 'billing' ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="ops-card space-y-3 text-sm">
            <h2 className="text-sm font-semibold">{copy.userDetail.billingAccount}</h2>
            {data.billing_account ? (
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-ops-muted">{copy.userDetail.planCreditsRemaining}</dt>
                  <dd>{formatNumber(data.billing_account.plan_credits_remaining)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ops-muted">{copy.userDetail.creditsBalance}</dt>
                  <dd>{formatNumber(data.billing_account.credits_balance)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-ops-muted">{copy.userDetail.bonusCredits}</dt>
                  <dd>{formatNumber(data.billing_account.bonus_credits_balance)}</dd>
                </div>
              </dl>
            ) : (
              <p className="text-ops-muted">{copy.userDetail.noBillingAccount}</p>
            )}
            {data.subscription ? (
              <div className="mt-4 border-t border-ops-border pt-4">
                <p className="mb-2 text-xs font-semibold uppercase text-ops-muted">
                  {copy.userDetail.subscription}
                </p>
                <p>
                  {copy.userDetail.subscriptionPlan}:{' '}
                  <StatusBadge status={data.subscription.plan_id} kind="plan" />
                </p>
                <p className="mt-1">
                  {copy.userDetail.subscriptionStatus}:{' '}
                  <StatusBadge status={data.subscription.status} kind="subscription" />
                </p>
                <p className="mt-1 text-ops-muted">
                  {copy.userDetail.periodEnd}: {formatDate(data.subscription.period_end)}
                </p>
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            {canAssignPlan() && scope === 'app' ? (
              <div className="ops-card">
                <h2 className="mb-4 text-sm font-semibold">{copy.userDetail.assignPlanTitle}</h2>
                <p className="mb-3 text-xs text-ops-muted">APP / назначение тарифа personal workspace</p>
                <form onSubmit={handleAssignPlan} className="space-y-3">
                  <div>
                    <label htmlFor="plan-id" className="mb-1 block text-xs text-ops-muted">
                      {copy.userDetail.assignPlanSelect}
                    </label>
                    <select
                      id="plan-id"
                      className="ops-input"
                      value={planId}
                      onChange={(e) => setPlanId(e.target.value)}
                    >
                      {(plansQuery.data?.items ?? []).map((plan) => (
                        <option key={plan.product_plan_id} value={plan.code}>
                          {formatPlan(plan.code)}
                          {plan.lineage === 'unlimited'
                            ? ` · fair use ${formatNumber(plan.fair_use_limit)}`
                            : ` · ${formatNumber(plan.included_credits)} кр.`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="plan-months" className="mb-1 block text-xs text-ops-muted">
                      {copy.userDetail.assignPlanMonths}
                    </label>
                    <input
                      id="plan-months"
                      type="number"
                      min={1}
                      max={36}
                      className="ops-input"
                      value={planMonths}
                      onChange={(e) => setPlanMonths(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="plan-reason" className="mb-1 block text-xs text-ops-muted">
                      {copy.userDetail.assignPlanReason}
                    </label>
                    <input
                      id="plan-reason"
                      type="text"
                      minLength={3}
                      className="ops-input"
                      value={planReason}
                      onChange={(e) => setPlanReason(e.target.value)}
                    />
                  </div>
                  {planError ? <p className="text-sm text-ops-danger">{planError}</p> : null}
                  {planSuccess ? <p className="text-sm text-ops-success">{planSuccess}</p> : null}
                  <button
                    type="submit"
                    disabled={assignPlanMutation.isPending}
                    className="ops-btn-primary"
                  >
                    {assignPlanMutation.isPending
                      ? copy.userDetail.assignPlanSubmitting
                      : copy.userDetail.assignPlanSubmit}
                  </button>
                </form>
              </div>
            ) : (
              <div className="ops-card text-sm text-ops-muted">
                {copy.userDetail.noAssignPlanPermission}
              </div>
            )}

            {isAppScope && canGrantCredits() ? (
              <div className="ops-card">
                <h2 className="mb-4 text-sm font-semibold">{copy.userDetail.grantTitle}</h2>
                <form onSubmit={handleGrant} className="space-y-3">
                  <div>
                    <label htmlFor="amount" className="mb-1 block text-xs text-ops-muted">
                      {copy.userDetail.grantAmount}
                    </label>
                    <input
                      id="amount"
                      type="number"
                      min={1}
                      className="ops-input"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="reason" className="mb-1 block text-xs text-ops-muted">
                      {copy.userDetail.grantReason}
                    </label>
                    <input
                      id="reason"
                      type="text"
                      minLength={3}
                      className="ops-input"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </div>
                  {formError ? (
                    <p className="text-sm text-ops-danger">{formError}</p>
                  ) : null}
                  {formSuccess ? (
                    <p className="text-sm text-ops-success">{formSuccess}</p>
                  ) : null}
                  <button type="submit" disabled={grantMutation.isPending} className="ops-btn-primary">
                    {grantMutation.isPending ? copy.userDetail.grantSubmitting : copy.userDetail.grantSubmit}
                  </button>
                </form>
              </div>
            ) : (
              <div className="ops-card text-sm text-ops-muted">
                {copy.userDetail.noGrantPermission}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {tab === 'sessions' ? (
        <div className="ops-card overflow-hidden p-0">
          {canRevokeSessions ? (
            <div className="border-b border-ops-border p-4">
              <button
                type="button"
                className="ops-btn-danger"
                disabled={revokeSessionsMutation.isPending}
                onClick={() => {
                  if (window.confirm(copy.userDetail.revokeSessionsConfirm)) {
                    revokeSessionsMutation.mutate();
                  }
                }}
              >
                {copy.userDetail.revokeSessions}
              </button>
            </div>
          ) : null}
          <table className="ops-table">
            <thead>
              <tr>
                <th>{copy.userDetail.sessionId}</th>
                <th>{copy.userDetail.sessionCreated}</th>
                <th>{copy.userDetail.sessionExpires}</th>
              </tr>
            </thead>
            <tbody>
              {data.sessions.map((session) => (
                <tr key={session.id}>
                  <td className="font-mono text-xs">{session.id}</td>
                  <td>{formatDate(session.created_at)}</td>
                  <td>{formatDate(session.expires_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === 'credits' ? (
        <div className="ops-card overflow-hidden p-0">
          {ledgerQuery.isLoading ? <LoadingState /> : null}
          {ledgerQuery.data ? (
            <table className="ops-table">
              <thead>
                <tr>
                  <th>{copy.userDetail.ledgerType}</th>
                  <th>{copy.userDetail.ledgerBucket}</th>
                  <th>{copy.userDetail.ledgerAmount}</th>
                  <th>{copy.userDetail.ledgerBalance}</th>
                  <th>{copy.userDetail.created}</th>
                </tr>
              </thead>
              <tbody>
                {ledgerQuery.data.items.map((row) => (
                  <tr key={row.id}>
                    <td>{row.type}</td>
                    <td>{row.bucket}</td>
                    <td>{formatNumber(row.amount)}</td>
                    <td>{formatNumber(row.balance_after)}</td>
                    <td className="text-ops-muted">{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}

      {tab === 'legal' ? (
        <div className="ops-card overflow-hidden p-0">
          <table className="ops-table">
            <thead>
              <tr>
                <th>{copy.userDetail.legalSource}</th>
                <th>{copy.userDetail.legalAccepted}</th>
                <th>{copy.userDetail.legalOffer}</th>
                <th>{copy.userDetail.legalPrivacy}</th>
                <th>{copy.userDetail.legalConsent}</th>
              </tr>
            </thead>
            <tbody>
              {data.legal_acceptances.map((row) => (
                <tr key={row.id}>
                  <td>{row.source}</td>
                  <td>{formatDate(row.accepted_at)}</td>
                  <td>{row.offer_version}</td>
                  <td>{row.privacy_version}</td>
                  <td>{row.consent_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === 'profile' && canDeleteUsers() ? (
        <div className="ops-card mt-4 border border-ops-danger/30">
          <h2 className="mb-2 text-sm font-semibold text-ops-danger">{copy.userDetail.deleteUserTitle}</h2>
          <p className="mb-4 text-sm text-ops-muted">{copy.userDetail.deleteUserHint}</p>
          <form onSubmit={handleDeleteUser} className="space-y-3">
            <div>
              <label htmlFor="delete-reason" className="mb-1 block text-xs text-ops-muted">
                {copy.userDetail.deleteUserReason}
              </label>
              <input
                id="delete-reason"
                type="text"
                minLength={3}
                className="ops-input"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="delete-confirm" className="mb-1 block text-xs text-ops-muted">
                {copy.userDetail.deleteUserConfirm}
              </label>
              <input
                id="delete-confirm"
                type="text"
                className="ops-input"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
              />
            </div>
            {deleteError ? <p className="text-sm text-ops-danger">{deleteError}</p> : null}
            <button
              type="submit"
              disabled={deleteUserMutation.isPending}
              className="ops-btn-danger"
            >
              {deleteUserMutation.isPending
                ? copy.userDetail.deleteUserSubmitting
                : copy.userDetail.deleteUserSubmit}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
